import { alpha } from '@mui/material/styles'
import { semantic, sand, amber, green, red } from './tokens/semantic'

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'

// SETUP COLORS
//
// primary is navy on purpose: it is the data-mark and focus color, not the
// action color. Primary *actions* are amber — they read the secondary ramp via
// the Button override, so painting data with primary.main never paints it with
// the CTA color and vice versa.
const PRIMARY = {
  lighter: '#D5E0F4',
  light: '#81A3DD',
  main: '#1D3A6C',
  dark: '#132748',
  darker: '#0A1324',
}
const PRIMARY_DARK = {
  lighter: '#c3d3ef',
  // The dark-mode focus ring reads primary.light (see overrides/Focus.ts);
  // #8FAEE0 is the lab's dark focus value.
  light: '#8FAEE0',
  main: '#A3BBE6',
  dark: '#7282a1',
  darker: '#4f5b70',
}
const SECONDARY = {
  lighter: '#FFF8E0',
  light: '#FFDF7C',
  main: amber[400],
  dark: amber[500],
  darker: '#FF8C00',
}
const INFO = {
  lighter: '#DDF3FD',
  light: '#7DD3F6',
  main: '#1EB2F0',
  dark: '#0B74A0',
  darker: '#042F40',
}
const SUCCESS = {
  lighter: green[100],
  light: green[300],
  main: green[600],
  // dark/darker are authored: one and two steps down from green.600 in the same
  // hue, used by Alert for text-on-tint. main on lighter is 5.68:1 (lab-measured).
  dark: '#1E5622',
  darker: '#173F1A',
}
const WARNING = {
  lighter: '#FFF3E0',
  light: '#FFB74D',
  main: '#F57C00',
  dark: '#E65100',
  darker: '#BF360C',
}
const ERROR = {
  lighter: red[100],
  light: red[300],
  main: red[600],
  // authored steps below red.600, same rationale as SUCCESS. main on white is 6.54:1.
  dark: '#8F1E18',
  darker: '#6B1712',
}

// The neutral ramp is the sand ramp: warm-tinted neutrals instead of cold
// grays. 500 is the tertiary-ink slot — the original #9e9d9b was 2.71:1 on white
// and failed everywhere it was used as text; see reference.ts for why the lab's
// replacement was darkened again. 700 is interpolated between sand.700 and
// sand.850 (the only slot without a lab value).
const GREY = {
  100: sand[50],
  200: sand[100],
  300: sand[200],
  400: sand[300],
  500: sand[500],
  600: sand[700],
  700: '#3E3A31',
  800: sand[900],
  900: sand[950],
}

/**
 * Translucent greys behind divider and action colours. Local on purpose: the Minimals template
 * published these on theme.palette.grey as 500_8, 500_24 and so on, which is not a shape MUI's
 * Color type has. Call alpha(theme.palette.grey[500], n) if you need one in a component.
 */
const GREY_ALPHA = {
  16: alpha(GREY[500], 0.16),
  24: alpha(GREY[500], 0.24),
  80: alpha(GREY[500], 0.8),
}

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: GREY[800] },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: '#fff' },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  action: {
    // hover is set per mode below. It used to live here as one shared dark wash,
    // which is invisible on a dark surface — dark mode needs a light overlay.
    disabled: GREY_ALPHA[80],
    disabledBackground: GREY_ALPHA[24],
    focus: GREY_ALPHA[24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
}

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: {
      primary: semantic.light.ink,
      secondary: semantic.light.ink2,
      tertiary: semantic.light.ink3,
      disabled: sand[400],
    },
    background: {
      paper: semantic.light.surface,
      default: semantic.light.bg,
      neutral: semantic.light.surface2,
      dark: sand[950],
    },
    divider: semantic.light.line,
    action: {
      active: GREY[600],
      ...COMMON.action,
      hover: semantic.light.hover,
      // Selection is the navy tint (semantic.ts). Never colour alone: a checked
      // control, navy text or medium weight goes with it.
      selected: semantic.light.selection,
    },
  },
  dark: {
    ...COMMON,
    primary: PRIMARY_DARK,
    success: { ...SUCCESS, main: semantic.dark.good, contrastText: GREY[800] },
    error: { ...ERROR, main: semantic.dark.danger, contrastText: GREY[800] },
    mode: 'dark',
    text: {
      primary: semantic.dark.ink,
      secondary: semantic.dark.ink2,
      tertiary: semantic.dark.ink3,
      disabled: sand[500],
    },
    background: {
      paper: semantic.dark.surface,
      default: semantic.dark.bg,
      neutral: semantic.dark.surface2,
      dark: sand[950],
    },
    divider: semantic.dark.line,
    action: {
      active: GREY[500],
      ...COMMON.action,
      hover: semantic.dark.hover,
      selected: semantic.dark.selection,
    },
  },
} as const

export default palette
